import React, { useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import {
  useCreateDataChangeRequestMutation,
  useEmployeeDataChangeRequestFormsQuery,
  FieldType,
  ObjectFieldValue,
  useEmployeeRecordFieldsQuery,
} from '../../../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { useToasts } from '../../../../../../context/Toast';
import { useUserInfo } from '../../../../context/UserInfo';
import { StepOne, StepOneData } from './StepOne';
import { StepTwo, StepTwoData } from './StepTwo';
import { StepThree } from './StepThree';
import { RecordField } from './types';
import { collectFiles } from '../../../../../../utils/file';

type NewChangeRequestModalProps = {
  show: boolean;
  recordType: string;
  showAllRequests: boolean;
  onHide: () => void;
};

export const ChangeRequestForm: React.FC<NewChangeRequestModalProps> = ({
  show,
  recordType,
  onHide,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const { id: currentUserId } = useUserInfo();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepTwoLastSubStep, setStepTwoLastSubStep] = useState(0);
  const [formData, setFormData] = useState<{
    stepOne?: StepOneData;
    stepTwo?: StepTwoData;
  }>({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );
  const [selectedEmployee, setSelectedEmployee] = useState<{
    Id: string;
    Name: string;
  } | null>(null);

  const { data, loading: loadingRequestForms } =
    useEmployeeDataChangeRequestFormsQuery();

  const [createDataChangeRequest, { loading: isSubmittingRequest }] =
    useCreateDataChangeRequestMutation();

  const selectedRequestForm = useMemo(() => {
    return data?.manager.requestForms.find(
      (form) => form.Id === formData.stepOne?.requestForm,
    );
  }, [data, formData.stepOne?.requestForm]);

  const objectFields = useMemo(() => {
    return (
      selectedRequestForm?.objectsFields.find(
        (field) => field.objectName === recordType,
      )?.fields || []
    );
  }, [selectedRequestForm, recordType]);

  const { data: recordFieldsData, loading: recordFieldsLoading } =
    useEmployeeRecordFieldsQuery({
      variables: {
        employeeId: selectedEmployeeId || '',
        recordType,
        fields: objectFields,
      },
      skip: !formData.stepOne?.requestForm || !selectedEmployeeId,
    });

  const handleStepOneSubmit = (submittedData: StepOneData) => {
    setFormData((prev) => ({ ...prev, stepOne: submittedData }));
    setCurrentStep(2);
  };

  const handleEmployeeSelect = (employeeId: string | null) => {
    setSelectedEmployeeId(employeeId);
    setFormData((prev) => ({
      ...prev,
      stepTwo: prev.stepTwo
        ? { ...prev.stepTwo, fields: {}, reason: '', files: [] }
        : undefined,
    }));
  };

  const handleStepTwoSubmit = (submittedData: StepTwoData) => {
    const employee = data?.manager.employees.find(
      (emp) => emp.Id === submittedData.employeeId,
    );
    setSelectedEmployee(employee || null);
    setFormData((prev) => ({ ...prev, stepTwo: submittedData }));
    setCurrentStep(3);
  };

  const handleStepTwoSubStepChange = (subStep: number) => {
    setStepTwoLastSubStep(subStep);
  };

  const handleStepThreeSubmit = async () => {
    if (!formData.stepOne?.requestForm || !formData.stepTwo) {
      return;
    }

    const collectedFiles = await collectFiles(formData.stepTwo.files);

    const objectFieldChanges: any = {
      recordId: formData.stepTwo.employeeId,
      objectName: recordType,
      fieldChanges: Object.entries(formData.stepTwo.fields).map(
        ([fieldName, newValue]) => {
          const fieldInfo = recordFieldsData?.recordFields.find(
            (field) => field.fieldInfo.name === fieldName,
          );
          return mapFieldChangesToRequestPayload(
            fieldName,
            newValue,
            fieldInfo,
          );
        },
      ),
    };

    if (
      formData.stepTwo.positionChange?.newPositionId &&
      formData.stepTwo.positionChange?.currentPositionId &&
      formData.stepTwo.positionChange?.effectiveDate
    ) {
      objectFieldChanges.positionChange = {
        newPositionId: formData.stepTwo.positionChange.newPositionId,
        currentPositionId: formData.stepTwo.positionChange.currentPositionId,
        effectiveDate: formData.stepTwo.positionChange.effectiveDate,
        endDate: formData.stepTwo.positionChange.endDate || null,
      };
    }

    if (
      formData.stepTwo.salaryChange &&
      formData.stepTwo.salaryChange.newValue > 0 &&
      !formData.stepTwo.salaryChange.hasPermissionIssue
    ) {
      const {
        hasPermissionIssue,
        formData: _,
        ...salaryChangePayload
      } = formData.stepTwo.salaryChange;
      objectFieldChanges.salaryChange = salaryChangePayload;
    }

    const input = {
      requestFormId: formData.stepOne.requestForm,
      payload: {
        objectsFieldsChanges: [objectFieldChanges],
        changeReason: formData.stepTwo.reason,
        files: collectedFiles.map((file) => ({
          fileName: file.fileName,
          fileContentBase64: file.fileBase64,
        })),
      },
    };

    try {
      await createDataChangeRequest({
        variables: {
          input: {
            ...input,
            initiatorEmployeeId: currentUserId,
          },
        },
      });
      addSuccess(t('notifications.success'));
      setCurrentStep(1);
      setStepTwoLastSubStep(0);
      setFormData({});
      onHide();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const renderStep = () => {
    const selectedForm = data?.manager.requestForms.find(
      (form) => form.Id === formData.stepOne?.requestForm,
    );

    switch (currentStep) {
      case 1:
        return (
          <StepOne
            requestForms={data?.manager.requestForms || []}
            onSubmit={handleStepOneSubmit}
            onCancel={onHide}
            initialData={formData.stepOne}
            loading={loadingRequestForms}
          />
        );

      case 2:
        return (
          <StepTwo
            onSubmit={handleStepTwoSubmit}
            onBack={handleBack}
            initialData={formData.stepTwo}
            employees={data?.manager.employees || []}
            selectedRequestForm={selectedForm}
            recordFields={(recordFieldsData?.recordFields || []).map(
              mapToRecordField,
            )}
            recordFieldsLoading={recordFieldsLoading}
            onEmployeeSelect={handleEmployeeSelect}
            onSubStepChange={handleStepTwoSubStepChange}
            lastSubStep={stepTwoLastSubStep}
          />
        );

      case 3:
        return (
          <StepThree
            onSubmit={handleStepThreeSubmit}
            onBack={handleBack}
            formData={formData}
            isSubmitting={isSubmittingRequest}
            selectedRequestForm={selectedForm || null}
            recordFields={(recordFieldsData?.recordFields || []).map(
              mapToRecordField,
            )}
            selectedEmployee={selectedEmployee}
          />
        );
    }
  };

  const handleHide = () => {
    setCurrentStep(1);
    setStepTwoLastSubStep(0);
    setFormData({});
    onHide();
  };

  const handleRemoveEmployee = () => {
    setSelectedEmployeeId(null);
    setSelectedEmployee(null);
    setStepTwoLastSubStep(0);
    setFormData((prev) => ({
      ...prev,
      stepTwo: undefined,
    }));
    setCurrentStep(1);
  };

  const selectedEmployeeName =
    data?.manager.employees.find((emp) => emp.Id === selectedEmployeeId)
      ?.Name || selectedEmployee?.Name;

  return (
    <Modal show={show} onHide={handleHide} centered size="lg">
      <Modal.Header closeButton={!isSubmittingRequest}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <Modal.Title className="fs-2">
              {currentStep === 1
                ? t('title')
                : selectedRequestForm?.Name || t('title')}
            </Modal.Title>
            <div className="text-muted fs-5 mt-1">
              {currentStep === 1 && t('steps.stepOnePlaceholder')}
              {currentStep === 2 && t('steps.stepTwoPlaceholder')}
              {currentStep === 3 && t('steps.stepThreePlaceholder')}
            </div>
          </div>
          {(currentStep === 2 || currentStep === 3) && selectedEmployeeName && (
            <div className="me-1">
              <span className="badge bg-primary d-flex align-items-center gap-3 fs-5 py-3 px-4">
                {selectedEmployeeName}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{ fontSize: '1rem' }}
                  onClick={handleRemoveEmployee}
                  disabled={isSubmittingRequest}
                />
              </span>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>{renderStep()}</Modal.Body>
    </Modal>
  );
};

export const mapToRecordField = (src: ObjectFieldValue): RecordField => ({
  fieldInfo: {
    name: src.fieldInfo.name,
    type: src.fieldInfo.type,
    label: src.fieldInfo.label,
    disabled: false,
    required: false,
    options: src.fieldInfo.picklistValues.map((pickListItem) => ({
      label: pickListItem.label,
      value: pickListItem.value,
    })),
  },
  value: src.value,
});

const mapFieldChangesToRequestPayload = (
  fieldName: string,
  newValue: any,
  fieldInfo: ObjectFieldValue | undefined,
) => {
  let newFieldValue = newValue;

  if (fieldInfo?.fieldInfo.type === FieldType.Boolean) {
    newFieldValue = newValue.toString();
  }
  if (fieldInfo?.fieldInfo.type === FieldType.Date) {
    const date = new Date(newValue);
    newFieldValue = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
      .toISOString()
      .split('T')[0];
  }

  return {
    fieldName,
    fieldType: fieldInfo?.fieldInfo.type || 'String',
    newValue: newFieldValue,
  };
};
